import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { connect } from 'react-redux';
import { AuthProvider } from './lib/authContext';
import './App.css';
import './polyfills';  // Add this at the top


// Lazy load components
const LoginPage = lazy(() => import('./views/login'));
const Map = lazy(() => import('./views/home'));

// Loading component
const LoadingFallback = () => (
  <div className="loading-fallback">
    <div className="loading-spinner">Loading...</div>
  </div>
);

// Protected Route Component
const ProtectedRoute = ({ isLoggedIn, children }) => {
  if (!isLoggedIn) {
    return <Navigate to="/login" replace />;
  }
  return <Suspense fallback={<LoadingFallback />}>{children}</Suspense>;
};

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Route Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="error-boundary">
          <h1>Something went wrong.</h1>
          <button onClick={() => window.location.reload()}>
            Reload Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Main App Component
function App({ isSignedIn }) {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <div className="App">
          <Router>
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
                <Route
                  path="/login"
                  element={
                    <Suspense fallback={<LoadingFallback />}>
                      <LoginPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/"
                  element={
                    <ProtectedRoute isLoggedIn={isSignedIn}>
                      <Map />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="*"
                  element={<Navigate to="/" replace />}
                />
              </Routes>
            </Suspense>
          </Router>
        </div>
      </AuthProvider>
    </ErrorBoundary>
  );
}

// Redux connection
const mapStateToProps = (state) => {
  const { isSignedIn } = state;
  return { isSignedIn };
};

export default connect(mapStateToProps, {})(App);
